import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AlertCircle, FileText, CreditCard, Settings, ArrowRight, User } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { getDashboardOptions } from '@/lib/client/@tanstack/react-query.gen'

export const Route = createFileRoute('/link/$parentId/')({
  component: ParentDashboard,
})

function ParentDashboard() {
  const { parentId } = Route.useParams()
  const hash = window.location.hash

  const { data: dashboard, isLoading } = useQuery({
    ...getDashboardOptions({
      path: { parentId },
    }),
  })

  const showPaymentSuccess = hash === '#payment-success'
  const showPaymentError = hash === '#payment-error'

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  const hasOutstanding = (dashboard?.totalBalance ?? 0) > 0

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Mon Espace Parent</h1>
          <p className="text-gray-600 mt-2">RevaCours - Gestion de vos factures et paiements</p>
        </div>

        {showPaymentSuccess && (
          <Alert className="mb-6 border-green-500 bg-green-50">
            <AlertCircle className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-900">Paiement confirmé !</AlertTitle>
            <AlertDescription className="text-green-800">
              <p>Votre paiement a été pris en compte. Le solde sera mis à jour d'ici quelques minutes.
              {hasOutstanding && dashboard && (
                <span className="block mt-2">
                  Il vous reste un solde de <strong>{dashboard.totalBalance.toFixed(2)}€</strong> à régulariser.
                </span>
              )}</p>
            </AlertDescription>
          </Alert>
        )}

        {showPaymentError && (
          <Alert className="mb-6 border-red-500 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertTitle className="text-red-900">Erreur de paiement</AlertTitle>
            <AlertDescription className="text-red-800">
              <p> Une erreur est survenue lors du traitement de votre paiement.
              Si le problème persiste, veuillez contacter RevaCours.</p>
            </AlertDescription>
          </Alert>
        )}

        {hasOutstanding && dashboard && (
          <Alert className="mb-6 border-orange-500 bg-orange-50">
            <AlertCircle className="h-4 w-4 text-orange-600" />
            <AlertTitle className="text-orange-900">Factures en attente</AlertTitle>
            <AlertDescription className="text-orange-800k">
              <p>Vous avez <strong>{dashboard.invoiceCount}</strong> facture(s) à régulariser pour un montant total de {" "}
              <strong>{dashboard.totalBalance.toFixed(2)}€</strong>.</p>
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-6 md:grid-cols-2 mb-8">
          <Card>
            <CardHeader>
              <CardTitle>Solde à régulariser</CardTitle>
              <CardDescription>Montant total en attente de paiement</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-gray-900">
                {dashboard?.totalBalance?.toFixed(2) || '0.00'}€
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {dashboard?.invoiceCount || 0} facture(s) en attente
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Actions rapides</CardTitle>
              <CardDescription>Gérer vos paiements</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button asChild className="w-full" size="lg" disabled={!hasOutstanding}>
                <Link to="/link/$parentId/pay" params={{ parentId }}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Régulariser maintenant
                </Link>
              </Button>
              {false && <Button asChild variant="outline" className="w-full">
                <Link to="/link/$parentId/payment-methods" params={{ parentId }}>
                  <Settings className="mr-2 h-4 w-4" />
                  Gérer mes moyens de paiement
                </Link>
              </Button>}
              <Button asChild variant="outline" className="w-full">
                <Link to="/link/$parentId/profil" params={{ parentId }}>
                  <User className="mr-2 h-4 w-4" />
                  Mon profil
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Dernières factures</CardTitle>
                <CardDescription>5 factures les plus récentes</CardDescription>
              </div>
              <Button asChild variant="ghost" size="sm">
                <Link to="/link/$parentId/invoices" params={{ parentId }}>
                  Tout voir
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {dashboard?.recentInvoices?.filter((inv: any) => inv.status !== 'DRAFT').length === 0 ? (
                <p className="text-sm text-gray-500">Aucune facture</p>
              ) : (
                <div className="space-y-3">
                  {dashboard?.recentInvoices?.filter((invoice: any) => invoice.status !== 'DRAFT').map((invoice: any) => (
                    <div key={invoice.id} className="flex items-center justify-between border-b pb-3 last:border-0">
                      <div className="flex items-center space-x-3">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium">{invoice.invoiceNumber}</p>
                          <p className="text-xs text-gray-500">
                            Du {new Date(invoice.billingPeriodStart).toLocaleDateString('fr-FR')} au{' '}
                            {new Date(invoice.billingPeriodEnd).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">{invoice.totalAmount.toFixed(2)}€</p>
                        <Badge variant={invoice.status === 'PAID' ? 'default' : invoice.status === 'PARTIALLY_PAID' ? 'secondary' : 'destructive'}>
                          {invoice.status === 'PAID' ? 'Payée' : invoice.status === 'PARTIALLY_PAID' ? 'Partielle' : 'En attente'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Derniers paiements</CardTitle>
                <CardDescription>5 paiements les plus récents</CardDescription>
              </div>
              <Button asChild variant="ghost" size="sm">
                <Link to="/link/$parentId/payments" params={{ parentId }}>
                  Tout voir
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {dashboard?.recentPayments?.length === 0 ? (
                <p className="text-sm text-gray-500">Aucun paiement</p>
              ) : (
                <div className="space-y-3">
                  {dashboard?.recentPayments?.map((payment: any) => (
                    <div key={payment.id} className="flex items-center justify-between border-b pb-3 last:border-0">
                      <div className="flex items-center space-x-3">
                        <CreditCard className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium">
                            {new Date(payment.createdAt).toLocaleDateString('fr-FR')}
                          </p>
                          <p className="text-xs text-gray-500">{payment.paymentMethod}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">{payment.amount.toFixed(2)}€</p>
                        <Badge variant={payment.status === 'SUCCEEDED' ? 'default' : payment.status === 'FAILED' ? 'destructive' : 'secondary'}>
                          {payment.status === 'SUCCEEDED' ? 'Réussi' : payment.status === 'FAILED' ? 'Échoué' : 'En cours'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
